import JSZip from "jszip";
import {
  MAX_BOOK_SIZE,
  MAX_EXTRACTED_TEXT,
  MAX_PDF_PAGES,
} from "../constants";
import type { ParsedPdf, PdfPage } from "../types";
import { parsePdf } from "./pdf";

export function bookType(file: File): "pdf" | "epub" {
  const name = file.name.toLocaleLowerCase();
  if (file.type === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (file.type === "application/epub+zip" || name.endsWith(".epub")) return "epub";
  throw new Error("Lütfen PDF veya EPUB formatında bir kitap seç.");
}

export function validateBook(file: File): void {
  bookType(file);
  if (file.size === 0) throw new Error("Bu dosya boş görünüyor.");
  if (file.size > MAX_BOOK_SIZE) {
    throw new Error("Kitap dosyası en fazla 250 MB olabilir.");
  }
}

function resolvePath(baseFile: string, relativePath: string): string {
  const cleanRelativePath = relativePath.split("#")[0];
  let decodedPath = cleanRelativePath;
  try {
    decodedPath = decodeURIComponent(cleanRelativePath);
  } catch {
    // Keep the original href; malformed escapes will simply miss the ZIP entry.
  }
  const parts = baseFile.split("/");
  parts.pop();
  for (const part of decodedPath.split("/")) {
    if (!part || part === ".") continue;
    if (part === "..") parts.pop();
    else parts.push(part);
  }
  return parts.join("/");
}

async function parseEpub(
  file: File,
  onProgress?: (current: number, total: number) => void,
): Promise<ParsedPdf> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (bytes[0] !== 0x50 || bytes[1] !== 0x4b) {
    throw new Error("Dosyanın içeriği geçerli bir EPUB değil.");
  }

  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(bytes);
  } catch {
    throw new Error("EPUB arşivi açılamadı veya hasarlı.");
  }

  const containerXml = await zip.file("META-INF/container.xml")?.async("text");
  if (!containerXml) throw new Error("EPUB paket bilgisi bulunamadı.");
  const xml = new DOMParser().parseFromString(containerXml, "application/xml");
  const packagePath = xml.querySelector("rootfile")?.getAttribute("full-path");
  if (!packagePath) throw new Error("EPUB içerik paketi bulunamadı.");

  const packageXml = await zip.file(packagePath)?.async("text");
  if (!packageXml) throw new Error("EPUB içerik listesi okunamadı.");
  const packageDoc = new DOMParser().parseFromString(packageXml, "application/xml");
  const manifest = new Map<string, string>();
  packageDoc.querySelectorAll("manifest item").forEach((item) => {
    const id = item.getAttribute("id");
    const href = item.getAttribute("href");
    const mediaType = item.getAttribute("media-type") ?? "";
    if (id && href && /xhtml|html/i.test(mediaType)) manifest.set(id, href);
  });

  const chapterPaths = Array.from(packageDoc.querySelectorAll("spine itemref"))
    .map((item) => manifest.get(item.getAttribute("idref") ?? ""))
    .filter((href): href is string => Boolean(href))
    .slice(0, 500);
  if (chapterPaths.length === 0) throw new Error("EPUB içinde okunabilir bölüm yok.");

  const pages: PdfPage[] = [];
  let characterCount = 0;
  for (const [index, href] of chapterPaths.entries()) {
    const chapter = await zip.file(resolvePath(packagePath, href))?.async("text");
    if (!chapter) continue;
    const doc = new DOMParser().parseFromString(chapter, "text/html");
    doc.querySelectorAll("script,style,svg,noscript").forEach((node) => node.remove());
    const text = (doc.body?.textContent ?? "")
      .replace(/\s+/g, " ")
      .trim();
    if (!text) continue;
    characterCount += text.length;
    if (characterCount > MAX_EXTRACTED_TEXT) {
      throw new Error("EPUB çok fazla metin içeriyor; daha küçük bir kitap seç.");
    }
    pages.push({ pageNumber: pages.length + 1, text });
    onProgress?.(index + 1, chapterPaths.length);
  }

  if (pages.length === 0) throw new Error("EPUB içinde seçilebilir metin bulunamadı.");
  return {
    name: file.name,
    size: file.size,
    pageCount: pages.length,
    pages,
    format: "epub",
  };
}

export async function parseBook(
  file: File,
  onProgress?: (current: number, total: number) => void,
): Promise<ParsedPdf> {
  validateBook(file);
  if (bookType(file) === "pdf") return parsePdf(file, onProgress);
  return parseEpub(file, onProgress);
}
